import jwtDecode, { JwtPayload } from 'jwt-decode';
import { selector } from 'recoil';

import { IUserProduct } from '../../common/isPayPerAgent';
import { IUserAtom, Role, RoleLevel, user } from '../atoms/user';

export interface IUserSettings
  extends Record<string, string | number | boolean> {
  refreshToken: string;
  userid: number;
  email: string;
  role: Role;
  businessId: number;
  apikey: string;
  tier: 'Integrated' | 'Enterprise Unlimited' | 'Enterprise';
  onlyManagersCanAddPhoneNumbers: 0 | 1;
  onlyManagersCanViewCallReporting: 0 | 1;
  inTrial: 0 | 1;
  trialExpired: 0 | 1;
  cardDeclined: 0 | 1;
  canCallCoach: 0 | 1;
  canBeManaged: 0 | 1;
  canDeleteUsers: 0 | 1;
  overrideDnc: 0 | 1;
  machineDetectionEnabled: 0 | 1;
  hasBilling: 0 | 1;
  callRecord: 0 | 1;
  timezone: string;
  allowAgentPowerlist: 0 | 1;
  logLevel:
    | 'off'
    | 'fatal'
    | 'error'
    | 'warn'
    | 'info'
    | 'debug'
    | 'trace'
    | 'all';
}

export interface IPermissions {
  role: Role;
  roleLevel: number;
  canManage: boolean;
  canAddPhoneNumbers: boolean;
  canViewCallReporting: boolean;
  accountReadOnly: boolean;
  hasSetSuperadminPermissions: boolean;
  canViewSetSuperadminPermissions: boolean;
  cardDeclined: boolean;
  canViewDebugInformation: boolean;
  canViewBlockedTab: boolean;
  callRecord: boolean;
  allowAgentPowerlist: boolean;
  canAccessPowerlistProduct: boolean;
  canAccessCallRouting: boolean;
  starterLimitMessage: boolean;
  displayOW: boolean;
  canSetApiLevel: boolean;
  canViewAllUsagePlans: boolean;
  canGive25PercentDiscount: boolean;
  canInitiateContactSync: boolean;
  canViewJiraReport: boolean;
  canAccessCallTranscriptionProduct: boolean;
  shouldPopSuperAdminStats: boolean;
  isBusinessEligibleToAccessCallTranscriptions: boolean;
  enableFreeBillingTier: boolean;
  isMachineDetectionFeatureEnabled: boolean;
  inTrial: boolean;
  canManageAuditMetaData: boolean;
  canDownloadHistory: boolean;
}

export const getUserSettings = (user: IUserAtom | undefined) =>
  jwtDecode(user?.accessToken ?? '') as IUserSettings & JwtPayload;

const getIsKixieEmployee = (
  userSettings: { businessId: number } | undefined
): boolean =>
  Boolean(userSettings?.businessId === 995 || userSettings?.businessId === 157);

const DEVELOPER_USER_IDS = [
  // DO NOT EXPOSE THIS directly as a permission.
  92226, // Ben Shamsian
  1619, // Keith Muenze
  187,
  95269, // Nang Chan
  94740, // Omid Halavi
  110978, // Austin Liu
  82496, // Chris Marshall
  105203, // Eddy Chau
  117354, // Jeff Palmeri
  120188, // Richard Durazo
  146863, // Bryce Darling
  119335, // Alli Dimailig
  100775, //'engusrbas@kixie.com'
  100774, //'engusrint@kixie.com'
  100773, //'engusrent@kixie.com'
  100014, //'engmaint2@kixie.com'
  100013, //'engmaent2@kixie.com'
  100011, //'engmaent@kixie.com'
  99973, //'engadbas@kixie.com'
  99972, //'engadint@kixie.com'
  99971, //'engadent@kixie.com'
  99969, //'engsabas@kixie.com'
  99967, //'engsaint@kixie.com'
  99966, //'engsaent@kixie.com'
  124898, // Will Scholl
  187511, // Chris Lomeli
  202587, // Waqas Afzal
  131975, // Darren Eam
  210308, // Charlie Bleisch
  208600, // Thomas Carfano,
  218164, // Mary Crowley
  265386, // mbecerra@kixie.com
  274531, // abdul@kixie.com
  274537, // Iqra@kixie.com
  275440, // sheeza@kixie.com
  275441, // ayesha@kixie.com
  21594, // dko@kixie.com
];

export const getIsDeveloper = (
  userSettings: { userid: number; businessId: number } | undefined
): boolean =>
  typeof userSettings?.userid === 'number' &&
  getIsKixieEmployee(userSettings) &&
  DEVELOPER_USER_IDS.includes(userSettings?.userid);

/**
 * Selector used to call PHP
 * NOTE: can return with null if user storage data has not been set
 *
 * @returns PHP response
 */
export const permissionsSelector = selector({
  key: 'permissionsSelector',
  get: ({ get }): IPermissions => {
    const userData = get(user) as IUserAtom;

    const userSettings = getUserSettings(userData);

    const isKixieEmployee = getIsKixieEmployee(userSettings);

    const isDeveloper = getIsDeveloper(userSettings);

    const isStarter =
      !userData?.brandStatus10Dlc?.brandStatus ||
      userData?.brandStatus10Dlc?.brandStatus?.toLowerCase() === '' ||
      userData?.brandStatus10Dlc?.brandStatus?.toLowerCase() !== 'standard';
    // const businessNumbersCount =
    //   userData?.brandStatus10Dlc?.businessNumbersCount ?? 0;
    // DASH-108 - restricting sms & call history exports & scheduled reports for users in bid 55168
    const isRestrictedBID = Number(userSettings?.businessId) === 55168;

    return {
      role: userSettings?.role,
      roleLevel: RoleLevel[userSettings?.role],
      canManage:
        (userSettings?.role === Role.SUPERADMIN ||
          userSettings?.role === Role.MANAGER ||
          userSettings?.role === Role.ADMIN) &&
        !userSettings?.accountReadOnly,
      canAddPhoneNumbers:
        userSettings?.role === Role.SUPERADMIN ||
        userSettings?.role === Role.MANAGER ||
        userSettings?.role === Role.ADMIN ||
        (userSettings?.role === Role.USER &&
          +userSettings?.onlyManagersCanAddPhoneNumbers === 0 &&
          !userSettings?.accountReadOnly), // users allowed if flag off
      canViewCallReporting:
        userSettings?.role === Role.SUPERADMIN ||
        userSettings?.role === Role.MANAGER ||
        userSettings?.role === Role.ADMIN ||
        (userSettings?.role === Role.USER &&
          +userSettings?.onlyManagersCanViewCallReporting === 0), // users allowed if flag off
      accountReadOnly: +userSettings?.accountReadOnly === 1,
      cardDeclined:
        +userSettings?.cardDeclined === 1 &&
        (userSettings?.role === Role.SUPERADMIN ||
          userSettings?.role === Role.MANAGER ||
          userSettings?.role === Role.ADMIN),
      hasSetSuperadminPermissions:
        (userSettings?.role === Role.SUPERADMIN &&
          !!userData?.user?.setSuperadminPermissions) ||
        userSettings?.userid === 92226,
      canViewSetSuperadminPermissions:
        (userSettings?.role === Role.SUPERADMIN && isDeveloper) ||
        userSettings?.userid === 92226,
      canViewDebugInformation:
        userSettings?.role === Role.SUPERADMIN && isDeveloper,
      canGive25PercentDiscount:
        userSettings?.role === Role.SUPERADMIN && isDeveloper,
      canViewBlockedTab: userSettings?.role === Role.SUPERADMIN && isDeveloper,
      canSetApiLevel: userSettings?.role === Role.SUPERADMIN,
      callRecord: userSettings?.callRecord === 1, // if true can view call recording,
      allowAgentPowerlist: Boolean(userSettings?.allowAgentPowerlist),
      canInitiateContactSync:
        userSettings?.role === Role.SUPERADMIN && isDeveloper,
      canAccessPowerlistProduct: Boolean(true),
      canAccessCallRouting: Boolean(true),
      starterLimitMessage: isStarter,
      displayOW:
        Boolean(userData?.onboardingWalkthrough?.displayOnboarding) &&
        (userSettings?.role === Role.SUPERADMIN ||
          userSettings?.role === Role.MANAGER ||
          userSettings?.role === Role.ADMIN),
      canViewAllUsagePlans: Role.SUPERADMIN && isDeveloper,
      canViewJiraReport:
        userSettings?.role === Role.SUPERADMIN && isKixieEmployee,
      canAccessCallTranscriptionProduct: canAccessCallTranscriptionProduct(
        userSettings?.role
      ),
      shouldPopSuperAdminStats:
        Role.SUPERADMIN && userSettings?.userid != 92226,
      isBusinessEligibleToAccessCallTranscriptions: Boolean(true), // all BID's can now access Call Transcription
      // only Following BID's canna access Call Transcriptions for now
      // [ 157, 995, 1001, 11260, 12850, 18570, 20054, 21594, 22927, 23937, 28610, 29682, 33750, ].includes(userData?.businessid),
      enableFreeBillingTier: true,
      isMachineDetectionFeatureEnabled:
        process.env.VITE_APP_MACHINE_DETECTION === '1',
      /* &&
        // only Following BID's can access Powerlist machine Detect while we are in Beta
        [
          // Internal
          157, 995, 16113, 16114, 21594, 33750,
          // Sales Team
          35766, 38575, 38651, 39707, 40533, 40310, 41076, 41093, 41173, 41151, 39155, 41922,
          // CS Team
          29065, 22927, 21374, 28292, 31063, 38349, 27568, 36064, 26554, 27716, ].includes(userData?.businessid),
        */
      inTrial: userSettings?.inTrial === 1 && userSettings?.trialExpired === 0,
      canManageAuditMetaData:
        process.env.VITE_APP_ENV == 'prod'
          ? [
              92226, // Ben Shamsian
            ].includes(userSettings?.userid)
          : process.env.VITE_APP_ENV == 'qa' ||
            process.env.VITE_APP_ENV == 'dev'
          ? isDeveloper
          : false,
      canDownloadHistory: checkCanDownloadHistory(
        userSettings?.role,
        isRestrictedBID
      ),
    };
  },
});

export const canAccessCallTranscriptionProduct = (
  role: Role | string
): boolean =>
  !!role &&
  (role === Role.SUPERADMIN ||
    role === Role.MANAGER ||
    role === Role.ADMIN ||
    hasCallTranscriptionProduct());

const hasCallTranscriptionProduct = (): boolean => {
  try {
    const userProductsString = localStorage.getItem('userProducts');
    if (!userProductsString || userProductsString === 'undefined') {
      return false;
    }

    const userProducts = JSON.parse(userProductsString) as IUserProduct[];
    return (
      userProducts?.filter(
        (product) => product.productid === 29 && product.units >= 1
      ).length > 0
    );
  } catch (_err) {
    return false;
  }
};

export const checkCanDownloadHistory = (
  role: Role | string,
  isRestrictedBID: boolean
): boolean => {
  const isManagerOrAdmin =
    !!role &&
    (role === Role.SUPERADMIN || role === Role.MANAGER || role === Role.ADMIN);

  if (!isRestrictedBID) {
    return true; // unrestricted BID: everyone can download
  }

  return isManagerOrAdmin;
};
